import { forEach, isObject, trim, fromPairs } from 'lodash';
import axios from 'axios';
import config from 'config';
import { encode } from '../crypto/base64';
import { moment } from '../../utils';
import oidMap from './oidMap';
import cryptoConstants from 'constants/crypto';
import responseMapperInterceptor from '../API/responseMapperInterceptor';

const { vipnetConfig } = config;
const axiosInstance = axios.create();
axiosInstance.interceptors.response.use(...responseMapperInterceptor);
const isSSL = window.location.protocol === 'https:';
const host = `${isSSL ? 'https:' : 'http:'}//${vipnetConfig.host}`;
const servicePort = isSSL ? vipnetConfig.sslServicePort : vipnetConfig.httpServicePort;
const clientPort = isSSL ? vipnetConfig.sslClientPort : vipnetConfig.httpClientPort;

const SERVICE_URL = `${host}:${servicePort}${vipnetConfig.defaultPath}`;
const CLIENT_URL = `${host}:${clientPort}${vipnetConfig.defaultPath}`;
const STATE_OK = 'ok';
const SYSTEM_STATE_PAYLOAD = 'CheckLssPresence';
const TASK_TYPES = {
  VERSION: 'ComponentVersionTask',
  REGISTER: 'RegistrationDataTask',
  SYSTEM_STATE: 'SystemStateTask',
  SIGN: 'SignTask',
  VERIFY: 'VerifyTask',
  ENCRYPT: 'EncryptTask',
  DECRYPT: 'DecryptTask',
  SIGN_AND_ENCRYPT: 'SignAndEncryptTask',
  DECRYPT_AND_VERIFY: 'DecryptAndVerifyTask',
  SELECT_CERTIFICATE: 'SelectCertificateTask',
  CREATE_CERTIFICATE: 'CreateCertificateRequestTask',
  INSTALL_CERTIFICATE: 'InstallCertificateIssuedByRequestTask',
};

export default class VipnetService {
  static generateGuid() {
    let d = new Date().getTime();
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
      let r = (d + Math.random() * 16) % 16 | 0;
      d = Math.floor(d / 16);
      return (c === 'x' ? r : (r & 0x7) | 0x8).toString(16);
    });
  }

  request({
    method = 'POST',
    url,
    params,
    responseType = 'json',
    data,
    headers = {
      'Content-Type': 'application/json;charset=UTF-8',
    },
  }) {
    const requestURL = `${url}/${method}`;

    return axiosInstance({
      method,
      url: requestURL,
      params,
      data,
      responseType,
      headers,
      transformResponse: [
        function (data) {
          let json;
          try {
            json = JSON.parse(data);
          } catch (e) {
            return data;
          }

          return json;
        },
      ],
    });
  }

  async sendRequest(options, isService) {
    const url = isService ? SERVICE_URL : CLIENT_URL;
    const requestOptions = Object.assign({}, options, {
      url,
      params: { type: options.data.taskType },
    });
    const response = await this.request(requestOptions);
    if (!response.isOk) {
      throw `Request error: ${response.error}`;
    }

    return response.data;
  }

  async checkRegistration(inn) {
    const componentsResponse = await this.getComponentsVersion();
    const components = componentsResponse.Components;

    if (!this.checkInstalledComponents(components)) {
      throw 'Vipnet is not installed';
    }

    return await this.setRegistrationData(inn);
  }

  checkInstalledComponents(components) {
    let installed = [false, false, false, false];
    const required = ['CRYPTOPROCSP', 'VIPNETCSP', 'LSS', 'REGISTRATIONAGENT'];

    forEach(components, (value) => {
      const found = required.indexOf(value.Name);
      if (~found) {
        installed[found] = true;
      }
    });

    return (installed[0] || installed[1]) && installed[2] && installed[3];
  }

  getComponentsVersion() {
    const data = {
      RequestId: VipnetService.generateGuid(),
      taskType: TASK_TYPES.VERSION,
    };

    return this.sendRequest({ data }, true);
  }

  setRegistrationData(userId) {
    //user inn
    const data = {
      RequestId: VipnetService.generateGuid(),
      taskType: TASK_TYPES.REGISTER,
      UserId: userId,
      SerialNumber: vipnetConfig.serialNumber,
    };

    return this.sendRequest({ data }, true);
  }

  async generateCertificate(subject) {
    const data = {
      RequestId: VipnetService.generateGuid(),
      taskType: TASK_TYPES.CREATE_CERTIFICATE,
      ExtendedKeyUsages: vipnetConfig.extendedKeyUsages,
      ByPassVisualization: true, //show password dialog
      Subject: subject,
    };

    const response = await this.sendRequest({ data });

    if (response.IsSuccessful) {
      return response.RawData;
    } else {
      throw response.ErrorMessage;
    }
  }

  async checkConnection() {
    const data = {
      RequestId: VipnetService.generateGuid(),
      taskType: TASK_TYPES.SYSTEM_STATE,
      Payload: SYSTEM_STATE_PAYLOAD,
    };

    const response = await this.sendRequest({ data });

    if (response.State === STATE_OK) {
      return response;
    } else {
      throw 'VipNet connection Error';
    }
  }

  async selectCertificate() {
    const data = {
      RequestId: VipnetService.generateGuid(),
      taskType: TASK_TYPES.SELECT_CERTIFICATE,
    };

    const response = await this.sendRequest({ data });

    if (!response.IsSuccessful) {
      throw response.ErrorMessage;
    }

    try {
      let certificate = JSON.parse(response.CertificateJson);
      certificate.ValidToDate = VipnetService.parseVipnetDate(certificate.NotAfter);
      certificate.ValidFromDate = VipnetService.parseVipnetDate(certificate.NotBefore);
      certificate.isValid = VipnetService.isValidCertificate(certificate);
      certificate.subject = certificate.Subject;
      certificate.subjectObj = VipnetService.parseSubject(certificate.Subject);
      certificate.subjectObj.INN =
        certificate.subjectObj.INN ||
        certificate.subjectObj['ИНН'] ||
        certificate.subjectObj[`OID.${oidMap.INN}`];
      certificate.subjectObj.OGRN =
        certificate.subjectObj.OGRN ||
        certificate.subjectObj['ОГРН'] ||
        certificate.subjectObj[`OID.${oidMap.OGRN}`];
      certificate.hasGOSTAlgorithm = cryptoConstants.GOST_ITEX_ALGORITHM.includes(
        certificate.SignatureAlgorithm.Value,
      );
      certificate.HasPrivateKey = true; //todo: check private key existence

      return certificate;
    } catch (exception) {
      throw `Error getting certificate: ${exception}`;
    }
  }

  async installCertificate(certificate) {
    const data = {
      RequestId: VipnetService.generateGuid(),
      taskType: TASK_TYPES.INSTALL_CERTIFICATE,
      Certificate: certificate,
    };

    const response = await this.sendRequest({ data });

    if (response.IsSuccessful) {
      return response;
    } else {
      throw response.ErrorMessage;
    }
  }

  async signCreate(certificate, dataToSign, options = {}) {
    const data = {
      RequestId: VipnetService.generateGuid(),
      taskType: TASK_TYPES.SIGN,
      DataToSign: options.isEncodedData ? dataToSign : encode(dataToSign),
      SignCertificate: isObject(certificate) ? certificate.Base64RawData : certificate,
      IsAttached: options.isAttached || false, // create detached sign by default
      ViewDescriptor: { FileExtension: options.extension || '' },
      DocumentName: options.fileName || '',
      Description: options.description || '',
      ByPassVisualization: true,
    };

    const response = await this.sendRequest({ data });
    if (response.IsSuccessful) {
      return response.SignedData;
    } else {
      throw response.ErrorMessage;
    }
  }

  static isValidCertificate(certificate) {
    const currentDate = moment();
    return currentDate.isBetween(certificate.ValidFromDate, certificate.ValidToDate, null, '()');
  }

  static parseSubject(subjectString) {
    let subjectArray = subjectString.split(',');

    if (subjectArray.length === 1) {
      subjectArray = subjectString.split(';');
    }

    return fromPairs(
      subjectArray.map((item) => {
        const clearedItem = trim(item);

        return clearedItem.split('=');
      }),
    );
  }

  static parseVipnetDate(dateString = '') {
    const pattern = /\/.+\((\d+)\)\//i;
    const matchArray = pattern.exec(dateString);

    if (!matchArray) {
      throw 'Invalid date format';
    }

    return moment(+matchArray[1]);
  }
}
